import { MaterialIcons } from "@expo/vector-icons";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import _ from "lodash";
import {
  Box,
  Button,
  Center,
  Divider,
  HStack,
  Icon,
  IconButton,
  Input,
  Pressable,
  ScrollView,
  Spinner,
  Text,
  useTheme,
  VStack,
} from "native-base";
import queryString from "query-string";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dimensions, FlatList } from "react-native";
import { Modalize } from "react-native-modalize";
import { useSelector } from "react-redux";
import * as URI from "uri-js";

import RecipeCardSkeleton from "../components/RecipeCardSkeleton";
import SimpleRecipeCard from "../components/SimpleRecipeCard";
import { Routes } from "../constants";
import { MealType } from "../constants/data";
import { formatMealType } from "../helpers/generalHelpers";
import { addSimpleRecipeToPlanner } from "../helpers/simpleRecipeHelpers";
import type { RootStackParamList } from "../navigation/NavigationStackParams";
import backendApi from "../services/backendApi";
import type {
  CalendarDay,
  MealSlotSpecification,
  MealTypeEnum,
  SimpleRecipe,
  SimpleRecipeMeal,
  UserProfile,
} from "../services/backendTypes";
import { userSelector } from "../slices/userSlice";

const {
  useFoodSimpleRecipeSuggestionsRetrieveQuery,
  usePlannerPlanSimpleRecipeCreateMutation,
  usePlannerCalendarItemDestroyMutation,
  useFoodSimpleRecipeCustomTagListQuery,
} = backendApi;

// Preparation time filter values that match backend API
type PrepTimeValue = "lte_5" | "lte_10" | "lte_20" | "lte_30" | "lte_60" | "any";

type SimpleRecipeMealSearchByPageInternal = {
  [number: number]: SimpleRecipeMeal[];
};
type SimpleRecipeMealSearchByPage = {
  byPage: SimpleRecipeMealSearchByPageInternal;
};

type Props = {
  navigation: NativeStackNavigationProp<RootStackParamList, Routes.SimpleRecipeSearchScreen>;
  route: {
    params: {
      mealSlotSpecification: MealSlotSpecification;
      currentCalendarDay: CalendarDay;
      calendarItemToReplace?: number;
      customTags?: number[];
      hideSearchBar?: boolean;
      tagTitle?: string;
      showFavorites?: boolean;
    };
  };
};

// Helper function to convert SimpleRecipe to SimpleRecipeMeal format
const convertToSimpleRecipeMeal = (simpleRecipe: SimpleRecipe): SimpleRecipeMeal => ({
  id: simpleRecipe.id,
  simple_recipe: simpleRecipe,
  meal_servings: simpleRecipe.servings || 1,
  // Calculate nutrition based on servings
  total_calories: simpleRecipe.total_calories,
  total_protein: simpleRecipe.protein_grams,
  total_carbohydrates: simpleRecipe.carbohydrates_grams,
  total_fat: simpleRecipe.fat_grams,
  total_fiber: simpleRecipe.fiber_grams || null,
});

const simpleRecipeMealSearchInitialState = { byPage: {} };

const SimpleRecipeSearchScreen = ({
  navigation,
  route: {
    params: {
      mealSlotSpecification,
      currentCalendarDay,
      calendarItemToReplace,
      customTags, // NEW
      hideSearchBar, // NEW
      tagTitle, // NEW
      showFavorites, // NEW
    },
  },
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const theme = useTheme();

  // Get user from Redux
  const user = useSelector(userSelector);

  const MEAL_TYPE_LIST = (Object.keys(MealType) as Array<keyof typeof MealType>).map((mealType, i) => ({
    id: i,
    label: formatMealType(MealType[mealType as MealTypeEnum], t),
    value: MealType[mealType],
  }));

  const [page, setPage] = useState<number>(1);
  const [recipeMealsByPage, setRecipeMealsByPage] = useState<SimpleRecipeMealSearchByPage>(
    simpleRecipeMealSearchInitialState
  );
  const [searchText, setSearchText] = useState("");
  const [inputValue, setInputValue] = useState(""); // Track input value separately for controlled input
  const [hasMorePages, setHasMorePages] = useState<boolean>(true);
  const [nextPageToFetch, setNextPageToFetch] = useState<number | null>(null);

  // Filter modal ref
  const filterModalRef = useRef<Modalize>(null);
  const { height: screenHeight } = Dimensions.get("window");

  // Filter values (temporary state before applying)
  // Don't pre-select any meal type if coming from inspiration screen (indicated by negative ID)
  const initialMealTypes = mealSlotSpecification.id < 0 ? [] : [mealSlotSpecification.meal_type as MealTypeEnum];
  // Initialize tags with customTags from route params if provided
  const initialTags = customTags || [];

  const [tempSelectedMealTypes, setTempSelectedMealTypes] = useState<MealTypeEnum[]>(initialMealTypes);
  const [tempSelectedTags, setTempSelectedTags] = useState<number[]>(initialTags);
  const [tempPrepTime, setTempPrepTime] = useState<PrepTimeValue>("any");
  const [tempFavorites, setTempFavorites] = useState<boolean | null>(showFavorites === true ? true : null);

  // Applied filter state (what's actually being used in the query)
  const [appliedMealTypes, setAppliedMealTypes] = useState<MealTypeEnum[]>(initialMealTypes);
  const [appliedTags, setAppliedTags] = useState<number[]>(initialTags);
  const [appliedPrepTime, setAppliedPrepTime] = useState<PrepTimeValue>("any");
  const [appliedFavorites, setAppliedFavorites] = useState<boolean | null>(showFavorites === true ? true : null);

  // NOTE: This is here to set the state of the search results dirty so we can use useEffect properly
  const [searchCounter, setSearchCounter] = useState<number>(0);
  const [isFilterChangeLoading, setIsFilterChangeLoading] = useState<boolean>(false);
  const resetPage = (): void => {
    setPage(1);
    setSearchCounter(searchCounter + 1);
  };

  const resetRecipeMealSearch = (): void => {
    resetPage();
    setRecipeMealsByPage(simpleRecipeMealSearchInitialState);
    setHasMorePages(true);
  };

  const setRecipeMealSearch = ({ results }: { results: SimpleRecipe[] }): void => {
    const convertedResults = results.map(convertToSimpleRecipeMeal);
    setRecipeMealsByPage((prevState) => ({
      byPage: {
        ...prevState.byPage,
        [page]: convertedResults,
      },
    }));
  };

  // Construct query parameters for the simple recipe suggestions endpoint
  const searchQueryArgs = {
    page,
    search: searchText.length >= 3 ? searchText : undefined,
    // Use proper array format for mealTypes
    mealTypes: appliedMealTypes.length > 0 ? appliedMealTypes : undefined,
    // Use only appliedTags which already contains the initial customTags from route params
    customTags: appliedTags.length > 0 ? appliedTags : undefined,
    totalTimeLte:
      appliedPrepTime !== "any"
        ? (() => {
            const parts = appliedPrepTime.split("_");
            return parts[1] ? parseInt(parts[1], 10) : undefined;
          })()
        : undefined, // Extract number from "lte_20" -> 20
    isFavorite: appliedFavorites !== null ? appliedFavorites : undefined,
    // Note: Suggestions endpoint auto-filters by user's dietary preferences & allergens
  };

  useEffect(() => {
    setIsFilterChangeLoading(true);
    setPage(1);
    setSearchCounter((prev) => prev + 1);
    setRecipeMealsByPage(simpleRecipeMealSearchInitialState);
    setHasMorePages(true);
    setNextPageToFetch(null);
  }, [searchText, appliedMealTypes, appliedTags, appliedPrepTime, appliedFavorites]);

  const {
    data,
    error,
    isLoading: isLoadingFirstTime,
    isFetching,
  } = useFoodSimpleRecipeSuggestionsRetrieveQuery(searchQueryArgs);

  // Pre-fetch next page query (only when we have a next page to fetch)
  const { data: nextPageData } = useFoodSimpleRecipeSuggestionsRetrieveQuery(
    nextPageToFetch
      ? {
          ...searchQueryArgs,
          page: nextPageToFetch,
        }
      : searchQueryArgs,
    {
      skip: !nextPageToFetch || !hasMorePages,
    }
  );

  useEffect(() => {
    if (!isFetching && data?.results) {
      setRecipeMealSearch({ results: data.results });
      setIsFilterChangeLoading(false);
      // Update hasMorePages based on the presence of next page
      setHasMorePages(!!data.next);

      // Set up pre-fetching for next page when we're on a page with more results
      if (data.next && page >= 1) {
        // Pre-fetch next page after a short delay to prioritize current page rendering
        const timer = setTimeout(() => {
          setNextPageToFetch(page + 1);
        }, 500);
        return () => clearTimeout(timer);
      }
      setNextPageToFetch(null);
    }
    return undefined;
  }, [data, searchCounter, isFetching, page]);

  // Handle pre-fetched data
  useEffect(() => {
    if (nextPageData?.results && nextPageToFetch === page) {
      // If we've navigated to the pre-fetched page, use its data
      setRecipeMealSearch({ results: nextPageData.results });
      setHasMorePages(!!nextPageData.next);
      setNextPageToFetch(null);
    }
  }, [nextPageData, nextPageToFetch, page]);

  const [planSimpleRecipe] = usePlannerPlanSimpleRecipeCreateMutation();
  const [deleteCalendarItemOnBackend] = usePlannerCalendarItemDestroyMutation();

  // Fetch tags for the current organization
  const { data: tagsData } = useFoodSimpleRecipeCustomTagListQuery({});

  const onPressBack = (): void => {
    navigation.goBack();
  };

  // Debounced function to update the search state
  const debouncedSearch = _.debounce((value: string): void => {
    resetPage();
    setSearchText(value);
  }, 200);

  // Handle input change - update input immediately, search debounced
  const onSearchTextChange = (value: string): void => {
    setInputValue(value); // Update input value immediately
    debouncedSearch(value); // Trigger debounced search
  };

  const onSimpleRecipePress = (simpleRecipeMeal: SimpleRecipeMeal): void => {
    navigation.push(Routes.SimpleRecipeDetailScreen, {
      simpleRecipeMeal,
      mealSlotSpecification,
      currentCalendarDay,
      onSelect: async () => {
        if (calendarItemToReplace) {
          // NOTE: Since this is used for the "list alternates" feature
          // if there is a calendarItemToReplace, we need to delete it first
          await deleteCalendarItemOnBackend({ id: calendarItemToReplace });
        }

        // Use the helper function that considers household_size
        await addSimpleRecipeToPlanner({
          recipe: simpleRecipeMeal.simple_recipe,
          mealSlot: mealSlotSpecification,
          currentCalendarDay,
          planSimpleRecipe: async (args) => planSimpleRecipe(args).unwrap(),
          refetchCalendarDays: () => {}, // No need to refetch since we navigate away
          navigation,
        });
      },
    });
  };

  // NOTE: Favorites toggle commented out as not supported by API
  // const onFavouritesToggle = (): void => {
  //   resetPage();
  //   setSelectFavourites(!selectFavourites);
  // };

  const mealTypeOptions = React.useMemo<Array<{ value: MealTypeEnum; label: string }>>(
    () => [
      { value: "BREAKFAST", label: formatMealType("BREAKFAST", t) },
      { value: "LUNCH", label: formatMealType("LUNCH", t) },
      { value: "DINNER", label: formatMealType("DINNER", t) },
      { value: "SNACK", label: formatMealType("SNACK", t) },
    ],
    [t]
  );

  const prepTimeOptions = React.useMemo(
    () => [
      { value: "lte_5", label: t("recipe_search.prep_time_1_5") },
      { value: "lte_10", label: t("recipe_search.prep_time_1_10") },
      { value: "lte_20", label: t("recipe_search.prep_time_1_20") },
      { value: "lte_30", label: t("recipe_search.prep_time_1_30") },
      { value: "lte_60", label: t("recipe_search.prep_time_1_60") },
      { value: "any", label: t("recipe_search.prep_time_any") },
    ],
    [t]
  );

  const handleApplyFilters = (): void => {
    // Apply the filters
    setAppliedMealTypes(tempSelectedMealTypes);
    setAppliedTags(tempSelectedTags);
    setAppliedPrepTime(tempPrepTime);
    setAppliedFavorites(tempFavorites);

    // Reset search to apply new filters
    resetPage();

    // Close modal
    filterModalRef.current?.close();
  };

  const handleClearFilters = (): void => {
    // Clear all temp selections but preserve initial tags from route params
    setTempSelectedMealTypes([]);
    setTempSelectedTags(initialTags);
    setTempPrepTime("any");
    setTempFavorites(null);
  };

  const handleCloseModal = (): void => {
    filterModalRef.current?.close();
  };

  const FilterModalContent = (): JSX.Element => (
    <Box flex={1} bg="primary.10">
      {/* Header */}
      <Box w="100%" alignItems="center" px={4} pt={8} pb={4}>
        <Text fontSize="xl" fontFamily={theme.fonts.serif} color="function.50">
          Filters
        </Text>
      </Box>
      <Divider bg="function.20" />

      {/* Scrollable Content */}
      <ScrollView flex={1} px={4} py={4}>
        <VStack space={0}>
          {/* Favorite Filter */}
          <VStack space={3} py={4}>
            <Text fontSize="md" fontFamily={theme.fonts.body} fontWeight="500" color="function.50">
              {t("recipe_search.filter_favorites")}
            </Text>
            <HStack space={2}>
              <Button
                variant="outline"
                size="sm"
                onPress={() => setTempFavorites(tempFavorites === true ? null : true)}
                bg={tempFavorites === true ? "primary.50" : "primary.30"}
                borderColor={tempFavorites === true ? "primary.50" : "primary.30"}
                borderWidth={1}
                borderRadius={theme.borderRadii.button.default}
                _text={{
                  color: tempFavorites === true ? "primary.10" : "function.50",
                  fontWeight: tempFavorites === true ? "600" : "400",
                }}
                _pressed={{ opacity: 0.8 }}
              >
                {t("recipe_search.favorites_only")}
              </Button>
            </HStack>
          </VStack>

          <Divider bg="function.20" />

          {/* Meal Time Filter */}
          <VStack space={3} py={4}>
            <Text fontSize="md" fontFamily={theme.fonts.body} fontWeight="500" color="function.50">
              Meal time
            </Text>
            <HStack space={2} flexWrap="wrap">
              {mealTypeOptions.map((option) => (
                <Button
                  key={option.value}
                  variant="outline"
                  size="sm"
                  mb={2}
                  onPress={() => {
                    if (tempSelectedMealTypes.includes(option.value)) {
                      setTempSelectedMealTypes(tempSelectedMealTypes.filter((type) => type !== option.value));
                    } else {
                      setTempSelectedMealTypes([...tempSelectedMealTypes, option.value]);
                    }
                  }}
                  bg={tempSelectedMealTypes.includes(option.value) ? "primary.50" : "primary.30"}
                  borderColor={tempSelectedMealTypes.includes(option.value) ? "primary.50" : "primary.30"}
                  borderWidth={1}
                  borderRadius={theme.borderRadii.button.default}
                  _text={{
                    color: tempSelectedMealTypes.includes(option.value) ? "primary.10" : "function.50",
                    fontWeight: tempSelectedMealTypes.includes(option.value) ? "600" : "400",
                  }}
                  _pressed={{ opacity: 0.8 }}
                >
                  {option.label}
                </Button>
              ))}
            </HStack>
          </VStack>

          <Divider bg="function.20" />

          {/* Total Time Filter (Prep + Cook) */}
          <VStack space={3} py={4}>
            <Text fontSize="md" fontFamily={theme.fonts.body} fontWeight="500" color="function.50">
              {t("recipe_search.filter_prep_time")}
            </Text>
            <HStack space={2} flexWrap="wrap">
              {prepTimeOptions.map((option) => (
                <Button
                  key={option.value}
                  variant="outline"
                  size="sm"
                  mb={2}
                  onPress={() => setTempPrepTime(option.value as PrepTimeValue)}
                  bg={tempPrepTime === option.value ? "primary.50" : "primary.30"}
                  borderColor={tempPrepTime === option.value ? "primary.50" : "primary.30"}
                  borderWidth={1}
                  borderRadius={theme.borderRadii.button.default}
                  _text={{
                    color: tempPrepTime === option.value ? "primary.10" : "function.50",
                    fontWeight: tempPrepTime === option.value ? "600" : "400",
                  }}
                  _pressed={{ opacity: 0.8 }}
                >
                  {option.label}
                </Button>
              ))}
            </HStack>
          </VStack>

          {/* Tags Filter */}
          {Boolean(tagsData?.results?.length && tagsData.results.length > 0) && (
            <>
              <Divider bg="function.20" />
              <VStack space={3} py={4}>
                <Text fontSize="md" fontFamily={theme.fonts.body} fontWeight="500" color="function.50">
                  Tags
                </Text>
                <HStack space={2} flexWrap="wrap">
                  {tagsData?.results?.map((tag) => (
                    <Button
                      key={tag.id}
                      variant="outline"
                      size="sm"
                      mb={2}
                      onPress={() => {
                        if (tempSelectedTags.includes(tag.id)) {
                          setTempSelectedTags(tempSelectedTags.filter((tagId) => tagId !== tag.id));
                        } else {
                          setTempSelectedTags([...tempSelectedTags, tag.id]);
                        }
                      }}
                      bg={tempSelectedTags.includes(tag.id) ? "primary.50" : "primary.30"}
                      borderColor={tempSelectedTags.includes(tag.id) ? "primary.50" : "primary.30"}
                      borderWidth={1}
                      borderRadius={theme.borderRadii.button.default}
                      _text={{
                        color: tempSelectedTags.includes(tag.id) ? "primary.10" : "function.50",
                        fontWeight: tempSelectedTags.includes(tag.id) ? "600" : "400",
                      }}
                      _pressed={{ opacity: 0.8 }}
                    >
                      {tag.human_name}
                    </Button>
                  ))}
                </HStack>
              </VStack>
            </>
          )}
        </VStack>
      </ScrollView>
    </Box>
  );

  const FilterModalFooter = (): JSX.Element => (
    <Box bg="primary.10" borderTopWidth={1} borderTopColor="function.20" safeAreaBottom>
      <HStack w="100%" justifyContent="space-between" alignItems="center" px={4} py={4}>
        <Button
          variant="ghost"
          onPress={handleClearFilters}
          borderRadius={theme.borderRadii.button.default}
          _text={{ color: "function.40" }}
        >
          {t("general.clear")}
        </Button>
        <HStack space={2}>
          <Button
            variant="outline"
            onPress={handleCloseModal}
            borderColor="function.20"
            borderRadius={theme.borderRadii.button.default}
            _text={{ color: "function.40" }}
          >
            {t("general.cancel")}
          </Button>
          <Button
            onPress={handleApplyFilters}
            bg="primary.50"
            borderRadius={theme.borderRadii.button.default}
            _text={{ color: "primary.10", fontWeight: "600" }}
            _pressed={{ opacity: 0.8 }}
          >
            {t("general.apply")}
          </Button>
        </HStack>
      </HStack>
    </Box>
  );

  const headerComponent = React.useMemo(
    () => (
      <Box bg="primary.10">
        {/* Header Section */}
        <HStack alignItems="center" px={4} py={4} bg="primary.10">
          <IconButton
            onPress={onPressBack}
            icon={<Icon as={MaterialIcons} name="arrow-back" color="function.50" size="lg" />}
            variant="ghost"
            _pressed={{ bg: "transparent" }}
            borderRadius={theme.borderRadii.button.default}
          />
          <Text fontSize="2xl" fontFamily={theme.fonts.serif} fontWeight="400" color="function.50" flex={1} ml={2}>
            {tagTitle || t("recipe_search.recipes_title")}
          </Text>
        </HStack>

        <VStack bg="primary.10" px={4} pb={3} space={3}>
          {/* Search Input */}
          {!hideSearchBar && (
            <Box>
              <Input
                placeholder={t("recipe_search.search_prompt")}
                value={inputValue}
                onChangeText={onSearchTextChange}
                InputLeftElement={<Icon as={MaterialIcons} mx="2" size="md" name="search" color="function.30" />}
                testID="simple-recipe-search-input"
                borderRadius={theme.borderRadii.input.default}
                bg="primary.10"
                borderColor="function.30"
                borderWidth={1}
                height={12}
                fontSize="md"
                fontFamily={theme.fonts.body}
                placeholderTextColor="function.30"
                color="function.50"
                autoFocus
                _focus={{
                  borderColor: "function.50",
                  bg: "primary.10",
                }}
                _web={{
                  _focus: {
                    outlineStyle: "none",
                    shadow: "none",
                  },
                }}
              />
            </Box>
          )}

          {/* Filters Row */}
          <HStack alignItems="center">
            <Pressable
              onPress={() => {
                filterModalRef.current?.open();
              }}
              _pressed={{ opacity: 0.7, bg: "primary.20" }}
            >
              <HStack
                alignItems="center"
                space={2}
                px={3}
                py={2}
                bg="transparent"
                borderRadius={theme.borderRadii.button.default}
              >
                <Icon as={MaterialIcons} name="filter-list" size="sm" color="function.50" />
                <Text fontSize="md" fontFamily={theme.fonts.body} color="function.50">
                  {t("recipe_search.filter")}
                </Text>
              </HStack>
            </Pressable>
          </HStack>

          {/* Active Filters Display */}
          {(appliedMealTypes.length > 0 ||
            appliedTags.length > 0 ||
            appliedPrepTime !== "any" ||
            appliedFavorites !== null) && (
            <ScrollView horizontal showsHorizontalScrollIndicator={false} px={4} pb={2}>
              <HStack space={2} alignItems="center">
                {appliedMealTypes.map((mealType) => (
                  <Box key={mealType} bg="primary.30" px={3} py={1} borderRadius={theme.borderRadii.button.pill}>
                    <HStack alignItems="center" space={1}>
                      <Text fontSize="sm" color="function.50">
                        {formatMealType(mealType, t)}
                      </Text>
                      <Pressable
                        onPress={() => {
                          setAppliedMealTypes(appliedMealTypes.filter((type) => type !== mealType));
                          resetPage();
                        }}
                        hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                      >
                        <Icon as={MaterialIcons} name="close" size="xs" color="function.50" />
                      </Pressable>
                    </HStack>
                  </Box>
                ))}
                {appliedTags.map((tagId) => {
                  const tag = tagsData?.results?.find((item) => item.id === tagId);
                  return tag ? (
                    <Box key={tagId} bg="primary.30" px={3} py={1} borderRadius={theme.borderRadii.button.pill}>
                      <HStack alignItems="center" space={1}>
                        <Text fontSize="sm" color="function.50">
                          {tag.human_name}
                        </Text>
                        <Pressable
                          onPress={() => {
                            setAppliedTags(appliedTags.filter((id) => id !== tagId));
                            resetPage();
                          }}
                          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                        >
                          <Icon as={MaterialIcons} name="close" size="xs" color="function.50" />
                        </Pressable>
                      </HStack>
                    </Box>
                  ) : null;
                })}
                {appliedPrepTime !== "any" && (
                  <Box bg="primary.30" px={3} py={1} borderRadius={theme.borderRadii.button.pill}>
                    <HStack alignItems="center" space={1}>
                      <Text fontSize="sm" color="function.50">
                        {prepTimeOptions.find((opt) => opt.value === appliedPrepTime)?.label}
                      </Text>
                      <Pressable
                        onPress={() => {
                          setAppliedPrepTime("any");
                          resetPage();
                        }}
                        hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                      >
                        <Icon as={MaterialIcons} name="close" size="xs" color="function.50" />
                      </Pressable>
                    </HStack>
                  </Box>
                )}
                {appliedFavorites !== null && (
                  <Box bg="primary.30" px={3} py={1} borderRadius={theme.borderRadii.button.pill}>
                    <HStack alignItems="center" space={1}>
                      <Text fontSize="sm" color="function.50">
                        {appliedFavorites ? t("recipe_search.favorites_only") : t("recipe_search.favorites_any")}
                      </Text>
                      <Pressable
                        onPress={() => {
                          setAppliedFavorites(null);
                          resetPage();
                        }}
                        hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                      >
                        <Icon as={MaterialIcons} name="close" size="xs" color="function.50" />
                      </Pressable>
                    </HStack>
                  </Box>
                )}
                <Pressable
                  onPress={() => {
                    setAppliedMealTypes([]);
                    setAppliedTags(initialTags); // Preserve tags from route params
                    setAppliedPrepTime("any");
                    setAppliedFavorites(null);
                    resetPage();
                  }}
                >
                  <Text fontSize="sm" color="function.40" fontWeight="500">
                    {t("general.clear")}
                  </Text>
                </Pressable>
              </HStack>
            </ScrollView>
          )}
        </VStack>
      </Box>
    ),
    [
      t,
      onPressBack,
      mealSlotSpecification,
      MEAL_TYPE_LIST,
      theme,
      tagTitle,
      hideSearchBar,
      appliedMealTypes,
      appliedTags,
      appliedPrepTime,
      appliedFavorites,
      prepTimeOptions,
      tagsData,
      inputValue,
      onSearchTextChange,
      resetPage,
      initialTags,
    ]
  );

  const searchResultsList = _.map(Object.values(recipeMealsByPage.byPage).flat(), (simpleRecipeMeal) => ({
    ...simpleRecipeMeal,
    key: simpleRecipeMeal.id,
  }));

  const loadingAnotherPage = !isLoadingFirstTime && isFetching && page > 1;

  /**
   * This safely parses the next page value and then sets the next page.
   */
  const onEndReachedFunc = (): void => {
    // Prevent loading more if already fetching or no more pages
    if (isFetching || !hasMorePages || isFilterChangeLoading) {
      return;
    }

    if (data?.next) {
      const parsedNextUrl = URI.parse(data.next);

      if (parsedNextUrl.query) {
        const parsedUrlQuery = queryString.parse(parsedNextUrl.query);

        if (parsedUrlQuery.page && typeof parsedUrlQuery.page === "string") {
          const pageAsNumber = parseInt(parsedUrlQuery.page, 10);

          const pageAsNumberIsValid = Number.isInteger(pageAsNumber) && pageAsNumber > 1;
          if (pageAsNumberIsValid) {
            setPage(pageAsNumber);
            return;
          }
        }
      }

      throw new Error(`Failed to parse next url:${data.next}`);
    }
  };

  const renderItemFunction = ({
    item: simpleRecipeMeal,
    index,
  }: {
    item: SimpleRecipeMeal;
    index: number;
  }): JSX.Element => (
    <SimpleRecipeCard
      simpleRecipeMeal={simpleRecipeMeal}
      onButtonPress={() => onSimpleRecipePress(simpleRecipeMeal)}
      mealSlotSpecification={mealSlotSpecification}
      index={index}
    />
  );

  const renderSkeletonCards = (): JSX.Element => (
    <Box px={4}>
      <Box flexDirection="row" flexWrap="wrap" mx={-1}>
        {[...Array(8)].map((_item, index) => (
          <Box key={`skeleton-${index}`} width="50%">
            <RecipeCardSkeleton />
          </Box>
        ))}
      </Box>
    </Box>
  );

  // Show error state if there's an error
  if (error) {
    return (
      <Box flex={1} bg="primary.10" safeAreaTop>
        {headerComponent}
        <Center flex={1} px={6}>
          <Text fontSize="lg" color="red.500" textAlign="center">
            {t("general.generic_error_message")}
          </Text>
        </Center>
      </Box>
    );
  }

  return (
    <Box flex={1} bg="primary.10" safeAreaTop nativeID="simpleRecipeSearchScreen">
      {isLoadingFirstTime || isFilterChangeLoading ? (
        <VStack flex={1}>
          {headerComponent}
          <ScrollView flex={1} bg="primary.10">
            {renderSkeletonCards()}
          </ScrollView>
        </VStack>
      ) : (
        <FlatList
          ListHeaderComponent={headerComponent}
          ListFooterComponent={
            loadingAnotherPage ? (
              <Center py={4}>
                <Spinner color="primary.600" size="lg" />
              </Center>
            ) : null
          }
          data={searchResultsList}
          contentContainerStyle={{ flexGrow: 1, paddingBottom: 20 }}
          onEndReached={onEndReachedFunc}
          onEndReachedThreshold={0.5} // Trigger earlier to leverage pre-fetched data
          bounces
          renderItem={renderItemFunction}
          keyExtractor={(item) => String(item.id)}
          showsVerticalScrollIndicator={false}
          numColumns={2}
          columnWrapperStyle={{ paddingHorizontal: 16 }}
          ListEmptyComponent={() => (
            <Center flex={1} px={6} py={12} minH="300">
              {(isFetching || isLoadingFirstTime || isFilterChangeLoading) && searchText.length >= 3 ? (
                <VStack alignItems="center" space={4}>
                  <Spinner color="primary.600" size="lg" />
                  <Text fontSize="md" color="gray.500" textAlign="center" fontWeight="medium">
                    {t("recipe_search.searching_message")}
                  </Text>
                </VStack>
              ) : (
                // Only show "no results" if we're truly not loading and have no results
                !isFilterChangeLoading && (
                  <VStack alignItems="center" space={4} maxW="280">
                    <Icon as={MaterialIcons} name="search" size="5xl" color="function.30" />
                    <Text
                      fontSize="lg"
                      fontWeight="500"
                      fontFamily={theme.fonts.body}
                      color="function.50"
                      textAlign="center"
                    >
                      {t("recipe_search.no_results_title")}
                    </Text>
                    <Text
                      fontSize="sm"
                      fontFamily={theme.fonts.body}
                      color="function.40"
                      textAlign="center"
                      lineHeight="sm"
                    >
                      {t("recipe_search.no_results_message")}
                    </Text>
                  </VStack>
                )
              )}
            </Center>
          )}
          keyboardShouldPersistTaps="handled"
          testID="simpleRecipeResults-list"
          nativeID="simpleRecipeResults-list"
        />
      )}
      <Modalize
        ref={filterModalRef}
        modalHeight={screenHeight * 0.85}
        modalStyle={{
          backgroundColor: theme.colors.primary[10],
          borderTopLeftRadius: theme.borderRadii.modal.default,
          borderTopRightRadius: theme.borderRadii.modal.default,
        }}
        handlePosition="inside"
        handleStyle={{
          backgroundColor: theme.colors.function[30],
          width: 40,
          height: 4,
          marginTop: 16,
          marginBottom: 8,
        }}
        openAnimationConfig={{
          timing: { duration: 200 },
        }}
        closeAnimationConfig={{
          timing: { duration: 200 },
        }}
        withReactModal
        avoidKeyboardLikeIOS
        onOpen={() => {
          // Initialize temp filters when modal opens
          setTempSelectedMealTypes(appliedMealTypes);
          setTempSelectedTags(appliedTags);
          setTempPrepTime(appliedPrepTime);
          setTempFavorites(appliedFavorites);
        }}
        FooterComponent={<FilterModalFooter />}
      >
        <FilterModalContent />
      </Modalize>
    </Box>
  );
};

export default SimpleRecipeSearchScreen;
